import axios from 'axios';
import { getAuth, onAuthStateChanged, User } from "firebase/auth";

interface Window {
  env: {
    REACT_APP_API_URL?: string;
  };
}
declare const window: Window;

const api = axios.create({
    baseURL:process.env.REACT_APP_API_URL || 'https://nvupmmyd66.us-east-1.awsapprunner.com',
});

// Redirect to the payment page whenever the backend rejects a request
// because the child hasn't been paid for (402 Payment Required).
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const loc = (window as any).location as Location;
        if (error?.response?.status === 402 && !loc.pathname.startsWith('/app/payment')) {
            loc.href = '/app/payment';
        }
        return Promise.reject(error);
    }
);

// Add Firebase token to requests
// api.interceptors.request.use(async (config) => {
//     const auth = getAuth();
//     const user = auth.currentUser;
//     if (user) {
//         const token = await user.getIdToken();
//         config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
// });
async function getIdTokenSafe(): Promise<string> {
  return new Promise((resolve, reject) => {
    const auth = getAuth();

    // Wait until Firebase finishes restoring session
    onAuthStateChanged(
      auth,
      async (user: User | null) => {
        if (!user) {
          reject(new Error("No Active session, please login"));
          return;
        }
        try {
          const token = await user.getIdToken();
          resolve(token);
        } catch (err) {
          reject(err);
        }
      },
      reject
    );
  });
}

export interface WordResponse {
    words: Array<{
        word: string;
        sentence: string;
        type: string;
    }>;
}

export async function submitGrade(gradeName: string) {
    try {
        const response = await api.post('/generate_all_grade_audio/', { grade:gradeName });
        return response.data.audio_files;
    } catch (error) {
        console.error('Error fetching words:', error);
        throw error;
    }
}
export async function saveUserData( userDetails: Record<string, any>) {
    try {
        const auth = getAuth();
         const user = auth.currentUser;
         const idToken = await user?.getIdToken();
        const response = await api.post('/save-user-data/', {
            idToken,
            email: userDetails.email,
            name: userDetails.name,
            password: userDetails.password
        });
        return response.data;
    } catch (error) {
        console.error('Error saving user data:', error);
        throw error;
    }
    
}
    export async function addChild(child: { name: string; age: number; grade: string; gender?: string }) {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();
            const response = await api.post('/add_child/', {
                idToken,
                name: child.name,
                age: child.age,
                grade: child.grade.replace(/ grade$/i, '').trim(),
                gender: child.gender,
            });
            return response.data;
        } catch (error) {
            console.error('Error adding child:', error);
            throw error;
        }
    }
    export async function submitWords(results:{ word: string; user_input: string; type: string }[], childId: any, gradeName: any) {
        try {
             const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();
            const response = await api.post('/submit_words/', {
                idToken: idToken,
                child_id: childId,
                grade: gradeName,
                words: results,
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting words:', error);
            throw error;
        }
    }

    export async function fetchChild() {
        try {
         
            const idToken = await getIdTokenSafe();
            const response = await api.post('/get_children/', {
                idToken: idToken,
            });
            return response.data.children;
        } catch (error) {
            console.error('Error fetching child:', error);
            throw error;
        }
    }
    export async function fetchCompleteResult(childId: string, grade: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/complete_result/', {
                idToken,
                child_id: childId,
                grade,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching complete result:', error);
            throw error;
        }
    }
    export async function generateTextAudio(text: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/generate_text_audio/', {
                idToken,
                text,
            });
            return response.data;
        } catch (error) {
            console.error('Error generating text audio:', error);
            throw error;
        }
    }
    export async function deleteChild(childId: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.delete('/delete_child/', {
                data: {
                    idToken,
                    child_id: childId,
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error deleting child:', error);
            throw error;
        }
    }

    // Submit feedback form
    export async function submitFeedbackForm(feedback: Record<string, any>) {
        try {
            const idToken = await getIdTokenSafe();
            const payload = { idToken, ...feedback };
            const response = await api.post('/feedback/', payload);
            return response.data;
        } catch (error) {
            console.error('Error submitting feedback:', error);
            throw error;
        }
    }

    // Check if user is admin and get stats
    export async function getAdminStats() {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/admin/stats/', { idToken });
            return response.data;
        } catch (error) {
            console.error('Error fetching admin stats:', error);
            throw error;
        }
    }

    // Check if user is admin
    export async function checkIsAdmin() {
        try {
            const stats = await getAdminStats();
            return stats.isAdmin || false;
        } catch (error) {
            console.error('Error checking admin status:', error);
            return false;
        }
    }

    // Get admin feedbacks
    export async function getAdminFeedbacks() {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/admin/feedback/', { idToken });
            return response.data;
        } catch (error) {
            console.error('Error fetching admin feedbacks:', error);
            throw error;
        }
    }

    // Get all registered users (admin only)
    export async function getAllUsers() {
        try {
            const stats = await getAdminStats();
            return {
                count: stats.totalUsers || 0,
                users: stats.users || []
            };
        } catch (error) {
            console.error('Error fetching users:', error);
            // Return empty data if endpoint not implemented yet
            return { count: 0, users: [] };
        }
    }

    // Make a user admin (admin only)
    export async function makeUserAdmin(targetEmail: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/admin/make-admin/', {
                idToken,
                targetEmail
            });
            return response.data;
        } catch (error) {
            console.error('Error making user admin:', error);
            throw error;
        }
    }

    // Load a user's Learning Snapshot plus its answer tables, to render the
    // page exactly as the parent sees it (admin only). childId disambiguates
    // when the account has more than one child.
    export async function getAdminSnapshotPreview(parentEmail: string, childId?: string) {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/admin/snapshot/preview/', {
            idToken,
            parent_email: parentEmail,
            ...(childId ? { child_id: childId } : {}),
        });
        return response.data;
    }

    // Send a user's Learning Snapshot to any address (admin only). The
    // attachment is the page exactly as rendered: pagePdf (base64) is the
    // client's capture of the page, attached as-is.
    export async function sendAdminSnapshot(
        parentEmail: string,
        toEmail: string,
        pagePdf: string,
        childId?: string
    ) {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/admin/snapshot/email/', {
            idToken,
            parent_email: parentEmail,
            to_email: toEmail,
            ...(childId ? { child_id: childId } : {}),
            page_pdf: pagePdf,
        });
        return response.data;
    }

    // Get the admin's bypass-payment toggle state
    export async function getAdminBypassPayment() {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/admin/bypass-payment/', { idToken });
            return response.data;
        } catch (error) {
            console.error('Error fetching admin bypass payment:', error);
            return { isAdmin: false, enabled: false };
        }
    }

    // Turn the admin bypass-payment toggle on or off
    export async function setAdminBypassPayment(enabled: boolean) {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/admin/bypass-payment/set/', { idToken, enabled });
        return response.data;
    }

    // Get all sentences for speaking test
    export async function getSpeakingSentences(childId: string, grade: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/speaking/get_all_sentences/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching speaking sentences:', error);
            throw error;
        }
    }

    // Submit batch speaking recordings
    export async function submitSpeakingBatch(
        childId: string,
        grade: string,
        submissions: Array<{
            sentence_id: string;
            original_sentence: string;
            audio_base64: string;
            audio_format?: string;
            time_to_speak_ms?: number | null;
            attempt?: number;
        }>
    ) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/speaking/submit/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
                submissions,
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting speaking batch:', error);
            throw error;
        }
    }

    // Get complete speaking test results
    export async function getSpeakingCompleteResult(childId: string, grade: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/speaking/complete_result/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching speaking complete result:', error);
            throw error;
        }
    }

    // =============================================
    // COMPREHENSION / READING TEST API
    // =============================================

    export interface ComprehensionQuestion {
        id: string;
        question: string;
        options: string[];
    }

    export interface ComprehensionStory {
        story_id: string;
        title: string;
        story_text: string;
        story_audio_base64: string;
        audio_source: string;
        duration_estimate: string;
        questions: ComprehensionQuestion[];
        total_questions: number;
    }

    export interface ComprehensionStoriesResponse {
        grade: string;
        total_stories: number;
        total_questions: number;
        instructions: string;
        stories: ComprehensionStory[];
    }

    export interface ComprehensionAnswer {
        question_id: string;
        selected_index: number;
    }

    export interface StoryAnswers {
        story_id: string;
        answers: ComprehensionAnswer[];
    }

    export interface ComprehensionSubmitResponse {
        success: boolean;
        user_id: string;
        child_id: string;
        grade: string;
        test_id: string;
        total_questions: number;
        correct_answers: number;
        score: number;
        max_score: number;
        percentage: number;
        level: string;
        status: string;
        recommendation: string;
        results: Array<{
            story_id: string;
            story_title: string;
            questions: Array<{
                question_id: string;
                question: string;
                selected_index: number;
                selected_answer: string;
                correct_index: number;
                correct_answer: string;
                is_correct: boolean;
            }>;
        }>;
        message: string;
    }

    export interface ComprehensionCompleteResult {
        user_id: string;
        child_id: string;
        grade: string;
        test_timestamp: string;
        summary: {
            total_questions: number;
            correct_answers: number;
            percentage: number;
            level: string;
            status: string;
        };
        parent_summary: {
            overall_score: string;
            percentage: number;
            level: string;
            grade_placement: string;
            next_step: string;
            recommendation: string;
            note: string;
        };
        story_breakdown: Array<{
            story_id: string;
            story_title: string;
            correct: number;
            total: number;
            percentage: number;
            questions: Array<{
                question_id: string;
                question: string;
                selected_index: number;
                selected_answer: string;
                correct_index: number;
                correct_answer: string;
                is_correct: boolean;
            }>;
        }>;
        actions: Array<{
            label: string;
            type: string;
            action: string;
        }>;
    }

    // Get comprehension stories with questions
    export async function getComprehensionStories(childId: string, grade: string): Promise<ComprehensionStoriesResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/comprehension/get_stories/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching comprehension stories:', error);
            throw error;
        }
    }

    // Submit comprehension test answers
    export async function submitComprehensionTest(
        childId: string,
        grade: string,
        storyAnswers: StoryAnswers[]
    ): Promise<ComprehensionSubmitResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/comprehension/submit/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
                story_answers: storyAnswers,
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting comprehension test:', error);
            throw error;
        }
    }

    // Get complete comprehension test results
    export async function getComprehensionCompleteResult(childId: string, grade?: string): Promise<ComprehensionCompleteResult> {
        try {
            const idToken = await getIdTokenSafe();
            const payload: any = {
                idToken,
                child_id: childId,
            };
            if (grade) {
                payload.grade = grade.replace(/ grade$/i, '').trim();
            }
            const response = await api.post('/comprehension/complete_result/', payload);
            return response.data;
        } catch (error) {
            console.error('Error fetching comprehension complete result:', error);
            throw error;
        }
    }

    // =============================================
    // LOGIC TEST API
    // =============================================

    export interface LogicOption {
        index: number;
        text: string;
        image_url: string | null;
    }

    export interface LogicItem {
        item_id: string;
        item_number: string;
        item_type: string;
        question_text: string;
        difficulty: string;
        options: LogicOption[];
        image_url?: string;
    }

    export interface LogicTestResponse {
        success: boolean;
        test_id: string;
        grade: string;
        total_items: number;
        instructions: string;
        items: LogicItem[];
    }

    export interface LogicItemResponse {
        item_id: string;
        selected_answer_index: number;
        response_time_seconds: number;
    }

    export interface LogicSubmitResponse {
        success: boolean;
        test_id: string;
        student_id: string;
        grade: string;
        total_items: number;
        correct_answers: number;
        score: number;
        percentage: number;
        level: string;
        cognitive_tags: string[];
        tag_breakdown: Record<string, any>;
        reasoning_under_load_detected: boolean;
        trial_and_error_detected: boolean;
        strategy_shift_difficulty_detected: boolean;
        message: string;
        score_id: string;
    }

    export interface LogicCompleteResultResponse {
        success: boolean;
        student_id: string;
        test_id: string;
        grade: string;
        test_timestamp: string;
        summary: {
            total_items: number;
            correct_answers: number;
            percentage: number;
            level?: string;
            cognitive_tags: string[];
            tag_breakdown?: Record<string, number>;
        };
        parent_summary: {
            overall_score: string;
            percentage: number;
            performance_level: string;
            grade_placement: string;
            next_step: string;
            strengths: string[];
            areas_to_develop: string[];
            recommendation: string;
            note: string;
        };
        behavioral_signals?: {
            reasoning_under_load: boolean;
            trial_and_error: boolean;
            strategy_shift_difficulty: boolean;
        };
        actions: Array<{
            label: string;
            type: string;
            action: string;
        }>;
    }

    // Get logic test items
    export async function getLogicTest(childId: string, grade: string): Promise<LogicTestResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/logic/get_test/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching logic test:', error);
            throw error;
        }
    }

    // Submit single logic response
    export async function submitLogicResponse(
        childId: string,
        itemId: string,
        selectedAnswerIndex: number,
        responseTimeSeconds: number,
        attempts: number = 1,
        selfCorrected: boolean = false,
        explanationProvided: string = ""
    ) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/logic/submit_response/', {
                idToken,
                child_id: childId,
                item_id: itemId,
                selected_answer_index: selectedAnswerIndex,
                response_time_seconds: responseTimeSeconds,
                attempts,
                self_corrected: selfCorrected,
                explanation_provided: explanationProvided,
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting logic response:', error);
            throw error;
        }
    }

    // Submit full logic test
    export async function submitLogicTest(
        childId: string,
        grade: string,
        testId: string,
        responses: LogicItemResponse[]
    ): Promise<LogicSubmitResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/logic/submit_test/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
                test_id: testId,
                responses,
            });
            return response.data;
        } catch (error) {
            console.error('Error submitting logic test:', error);
            throw error;
        }
    }

    // Get complete logic test results
    // testId is optional: when omitted, the backend returns the latest logic result for the child
    // (used by the Learning Journey, which doesn't track a test_id).
    export async function getLogicCompleteResult(childId: string, testId: string | undefined, grade: string): Promise<LogicCompleteResultResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const payload: any = {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            };
            if (testId) {
                payload.test_id = testId;
            }
            const response = await api.post('/logic/complete_result/', payload);
            // Handle case where API returns 200 but success: false
            if (response.data && response.data.success === false) {
                throw new Error(response.data.error || 'No results found for this test');
            }
            return response.data;
        } catch (error) {
            console.error('Error fetching logic complete result:', error);
            throw error;
        }
    }

    // =============================================
    // PAYMENT API (Stripe Checkout)
    // =============================================

    // Pricing constants — display fallback only. The backend is the source of
    // truth and always recomputes prices server-side.
    export const FIRST_CHILD_PRICE_CENTS = 900;
    export const ADDITIONAL_CHILD_PRICE_CENTS = 300;

    export interface PaymentQuoteItem {
        child_id: string;
        name: string;
        grade: string;
        amount_cents: number;
    }

    export interface PaymentQuote {
        items: PaymentQuoteItem[];
        total_cents: number;
        currency: string;
    }

    export interface CheckoutSessionResponse {
        payment_id: string;
        checkout_url: string;
    }

    export interface PaymentStatusResponse {
        payment_id: string;
        status: 'pending' | 'completed' | 'failed' | 'expired';
        child_ids: string[];
    }

    export interface PaymentHistoryChild {
        child_id: string;
        name: string;
        grade: string;
        payment_status: 'paid' | 'unpaid';
    }

    export interface PaymentHistoryItem {
        payment_id: string;
        status: 'pending' | 'completed' | 'failed' | 'expired';
        amount_cents: number;
        currency: string;
        stripe_session_id: string;
        created_at: string;
        completed_at: string;
        children: PaymentHistoryChild[];
    }

    export interface PaymentHistoryResponse {
        total: number;
        completed: number;
        pending: number;
        expired: number;
        items: PaymentHistoryItem[];
    }

    // Get a server-computed price quote for the selected children
    export async function getPaymentQuote(childIds: string[]): Promise<PaymentQuote> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/payment/quote/', {
                idToken,
                child_ids: childIds,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching payment quote:', error);
            throw error;
        }
    }

    // Create a Stripe Checkout session for the selected children.
    // Returns the hosted checkout URL to redirect the parent to.
    export async function createCheckoutSession(childIds: string[]): Promise<CheckoutSessionResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const origin = ((window as any).location as Location).origin;
            const response = await api.post('/payment/create-checkout-session/', {
                idToken,
                child_ids: childIds,
                success_url: `${origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${origin}/payment/cancelled`,
            });
            return response.data;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            throw error;
        }
    }

    // Check the status of a payment (poll after returning from Stripe;
    // the webhook is what actually marks children as paid).
    export async function getPaymentStatus(params: { paymentId?: string; sessionId?: string }): Promise<PaymentStatusResponse> {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/payment/status/', {
                idToken,
                payment_id: params.paymentId,
                session_id: params.sessionId,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching payment status:', error);
            throw error;
        }
    }

    // Fetch the caller's full payment history with per-child details.
    export async function getPaymentHistory(): Promise<PaymentHistoryResponse> {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/payment/history/', { idToken });
        return response.data;
    }

    // =============================================
    // PROMO CODES (admin-generated, single-use, unlock one child)
    // =============================================

    export interface PromoCodeItem {
        code: string;
        status: 'active' | 'used';
        created_by: string;
        created_at: string;
        batch_id: string;
        used_by: string;
        used_for_child: string;
        used_at: string;
    }

    export interface PromoCodesListResponse {
        total: number;
        active: number;
        used: number;
        codes: PromoCodeItem[];
    }

    export interface GeneratePromoCodesResponse {
        success: boolean;
        count: number;
        batch_id: string;
        codes: string[];
    }

    export interface RedeemPromoCodeResponse {
        success: boolean;
        message: string;
        code: string;
        child_id: string;
        child_name: string;
    }

    // Admin: generate one or more single-use promo codes.
    export async function generatePromoCodes(count: number): Promise<GeneratePromoCodesResponse> {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/admin/promo-codes/generate/', { idToken, count });
        return response.data;
    }

    // Admin: list all promo codes with their usage status.
    export async function listPromoCodes(): Promise<PromoCodesListResponse> {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/admin/promo-codes/list/', { idToken });
        return response.data;
    }

    // Parent: redeem a promo code to unlock a single child without payment.
    export async function redeemPromoCode(code: string, childId: string): Promise<RedeemPromoCodeResponse> {
        const idToken = await getIdTokenSafe();
        const response = await api.post('/promo-codes/redeem/', { idToken, code, child_id: childId });
        return response.data;
    }

    export async function fetchFinalReport(childId: string, grade: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/final_report/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching final report:', error);
            throw error;
        }
    }

    export interface SnapshotEmailResponse {
        success: boolean;
        sent_to: string;
        message: string;
    }

    /**
     * Email the snapshot letter to a parent as a PDF attachment.
     *
     * `toEmail` is optional: left out, the backend sends it to the address on
     * the account. The dialog prefills that address and lets the parent change
     * it, so the same letter can go to a teacher or a second parent.
     *
     * `pagePdfBase64` is the page exactly as the parent sees it: a
     * client-rendered capture of the page, attached as-is.
     */
    export async function emailSnapshotLetter(
        childId: string,
        grade: string,
        toEmail: string | undefined,
        pagePdfBase64: string,
    ) {
        const idToken = await getIdTokenSafe();
        const response = await api.post<SnapshotEmailResponse>('/snapshot/email/', {
            idToken,
            child_id: childId,
            grade: grade.replace(/ grade$/i, '').trim(),
            to_email: toEmail?.trim() || undefined,
            // The page itself, as rendered — attached as-is.
            page_pdf: pagePdfBase64,
        });
        return response.data;
    }

    /** The "Dear Parent" snapshot letter shown on the final result page. */
    export async function fetchSnapshotLetter(childId: string, grade: string) {
        try {
            const idToken = await getIdTokenSafe();
            const response = await api.post('/snapshot/', {
                idToken,
                child_id: childId,
                grade: grade.replace(/ grade$/i, '').trim(),
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching snapshot letter:', error);
            throw error;
        }
    }
